import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LOGO = "/bailey-logo.png";
const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

function MethodCard({ m, canDelete, onDelete }) {
  return (
    <div
      className="bg-white border border-[#e6e2d6] rounded-2xl p-6 relative group text-center"
      data-testid={`payment-method-card-${m.id}`}
    >
      {canDelete && (
        <button
          onClick={() => onDelete(m)}
          className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white border border-stone-200 text-stone-400 hover:text-red-600 hover:border-red-300 opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
          title="Remove"
          data-testid={`payment-method-delete-${m.id}`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
      <div className="label-eyebrow mb-1">{m.label}</div>
      {m.handle && (
        <div className="font-serif text-xl text-[#1a2947] mb-4">{m.handle}</div>
      )}
      {m.qr_url && (
        <div className="bg-[#faf7ef] border border-[#eae5d9] rounded-xl p-4 flex items-center justify-center mb-4">
          <img
            src={API_BASE.replace(/\/api$/, "") + m.qr_url}
            alt={`${m.label} QR`}
            className="max-w-full h-auto"
            style={{ maxHeight: 280 }}
          />
        </div>
      )}
      {m.note && (
        <p className="text-sm text-stone-600 whitespace-pre-line">{m.note}</p>
      )}
    </div>
  );
}

export default function PaymentsPage() {
  const { user } = useAuth();
  const [methods, setMethods] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ label: "", handle: "", note: "", order: 0 });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = useCallback(async () => {
    try {
      const r = await api.get("/payment-methods");
      setMethods(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load payment methods");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) return toast.error("Please add a label (e.g., Venmo).");
    if (!file) return toast.error("Please attach the QR code image.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", form.label);
      fd.append("handle", form.handle);
      fd.append("note", form.note);
      fd.append("order", String(form.order || 0));
      await api.post("/payment-methods", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Payment method added");
      setForm({ label: "", handle: "", note: "", order: 0 });
      setFile(null);
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to add");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.label}?`)) return;
    try {
      await api.delete(`/payment-methods/${m.id}`);
      toast.success("Removed");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to remove");
    }
  };

  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      <header className="max-w-6xl w-full mx-auto px-6 md:px-10 pt-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img
            src={LOGO}
            alt="Bailey Family Reunion"
            className="h-10 w-10 rounded-full object-cover ring-2 ring-[#1a2947]/10"
          />
          <span className="font-serif text-2xl text-stone-900">Bailey Family Reunion</span>
        </Link>
        <Link
          to="/"
          className="label-eyebrow text-stone-500 hover:text-[#1a2947] flex items-center gap-1"
        >
          <ArrowLeft className="h-4 w-4" /> Back home
        </Link>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-14">
        <div className="text-center mb-12">
          <div className="text-[10px] tracking-[0.16em] uppercase font-semibold text-white bg-[#1a2947] px-3 py-1 rounded-full inline-block mb-4">
            Reunion dues
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-stone-900 tracking-tight">
            Send your payment
          </h1>
          <p className="text-stone-600 mt-3 max-w-xl mx-auto">
            Scan any QR code below to send your reunion dues.
          </p>
          {user && !showForm && (
            <Button
              onClick={() => setShowForm(true)}
              className="mt-6 rounded-full bg-[#1a2947] hover:bg-[#0f1a2e] btn-pill"
              data-testid="payments-add-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> Add payment method
            </Button>
          )}
        </div>

        {showForm && (
          <form
            onSubmit={submit}
            className="bg-white border border-[#e6e2d6] rounded-2xl p-6 md:p-8 mb-10 shadow-[0_20px_60px_-30px_rgba(26,41,71,0.20)]"
            data-testid="payments-form"
          >
            <div className="label-eyebrow mb-4">Add payment method</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              <div>
                <Label className="label-eyebrow">Label</Label>
                <Input
                  value={form.label}
                  onChange={set("label")}
                  required
                  placeholder="Venmo · Cash App · Zelle · PayPal"
                  className="mt-1 bg-stone-50 border-stone-200"
                  data-testid="payments-label"
                />
              </div>
              <div>
                <Label className="label-eyebrow">Handle / recipient</Label>
                <Input
                  value={form.handle}
                  onChange={set("handle")}
                  placeholder="@bailey-reunion"
                  className="mt-1 bg-stone-50 border-stone-200"
                  data-testid="payments-handle"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="label-eyebrow">QR code image</Label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="mt-1 block text-sm"
                  data-testid="payments-file"
                />
                <p className="text-xs text-stone-500 mt-1">
                  PNG or JPG, up to 10 MB. Take a screenshot of your Venmo/CashApp/Zelle QR.
                </p>
              </div>
              <div className="md:col-span-2">
                <Label className="label-eyebrow">Note (optional)</Label>
                <Textarea
                  value={form.note}
                  onChange={set("note")}
                  rows={2}
                  placeholder="Put 'Bailey Reunion' in the memo."
                  className="mt-1 bg-stone-50 border-stone-200"
                  data-testid="payments-note"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy}
                className="rounded-full bg-[#1a2947] hover:bg-[#0f1a2e] btn-pill"
                data-testid="payments-submit"
              >
                {busy ? "Uploading…" : (<><Upload className="h-4 w-4 mr-1" /> Save</>)}
              </Button>
            </div>
          </form>
        )}

        {methods.length === 0 ? (
          <div className="text-center text-stone-500 py-14">
            {user
              ? "No payment methods yet — add one to get started."
              : "Your organizer hasn't set up any payment methods yet."}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="payments-grid">
            {methods.map((m) => (
              <MethodCard key={m.id} m={m} canDelete={!!user} onDelete={remove} />
            ))}
          </div>
        )}

        <p className="text-center text-xs text-stone-400 mt-10">
          After you pay, contact your reunion organizer so they can mark you paid on your family card.
        </p>
      </main>
    </div>
  );
}
